import styled from 'styled-components';
import { Flex, Typography } from 'antd';
import { labelFormats } from '@page-components/warehouse/common';
import { COLOR } from '@styles/theme';

const { Text } = Typography;

const SAMPLE_HEADER = 'ZONE-001';
const SAMPLE_PALLET_NUMBER = 'BULK-001';
const SAMPLE_PARTS = { rack: 'RK-01', bay: '1', level: '1' };

/** Generic 1D barcode stripes for preview only (not scannable). */
const BAR_PATTERN = [
  2, 1, 3, 1, 2, 4, 1, 2, 3, 2, 1, 4, 2, 1, 3, 1, 2, 3, 2, 1, 2, 4, 1, 3,
];

const PreviewWrap = styled(Flex)`
  gap: 10px;
`;

const PreviewCard = styled.button<{ $active: boolean }>`
  flex: 1;
  min-width: 0;
  padding: 8px;
  border-radius: 6px;
  border: 1px solid ${({ $active }) => ($active ? COLOR.accent : COLOR.border)};
  background: ${COLOR.white};
  opacity: ${({ $active }) => ($active ? 1 : 0.7)};
  transition:
    border-color 0.15s,
    opacity 0.15s;
  cursor: pointer;
  text-align: inherit;

  &:hover {
    border-color: ${({ $active }) => ($active ? COLOR.accent : COLOR.textSecondary)};
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px solid ${COLOR.accent};
    outline-offset: 2px;
  }
`;

const PreviewCaption = styled(Text)`
  display: block;
  font-size: 11px;
  margin-bottom: 6px;
  text-align: center;
`;

const LabelFrame = styled.div`
  border: 1px solid ${COLOR.black};
  background: ${COLOR.white};
  text-align: center;
  overflow: hidden;
  pointer-events: none;
`;

const LabelRule = styled.hr`
  margin: 0;
  border: none;
  border-top: 1px solid ${COLOR.black};
`;

const LabelHeader = styled.div`
  padding: 5px 4px;
  font-size: 9px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0.02em;
`;

const LabelBody = styled.div`
  padding: 8px 6px 6px;
`;

const BarcodeRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 1px;
  height: 28px;
  margin: 0 auto 6px;
  max-width: 100%;
`;

const Bar = styled.span<{ $w: number }>`
  display: block;
  width: ${({ $w }) => $w}px;
  height: 100%;
  background: ${COLOR.black};
`;

const MainId = styled.div`
  font-size: 11px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0.04em;
`;

const DetailFooter = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  border-top: 1px solid ${COLOR.black};
`;

const DetailCell = styled.div`
  padding: 5px 2px;
  border-right: 1px solid ${COLOR.black};

  &:last-child {
    border-right: none;
  }
`;

const DetailLabel = styled.div`
  font-size: 7px;
  color: ${COLOR.textSecondary};
  line-height: 1.2;
  margin-bottom: 2px;
`;

const DetailValue = styled.div`
  font-size: 10px;
  font-weight: 700;
  line-height: 1.2;
`;

function GenericBarcode() {
  return (
    <BarcodeRow aria-hidden>
      {BAR_PATTERN.map((w, i) => (
        <Bar key={i} $w={w} />
      ))}
    </BarcodeRow>
  );
}

function MinimalLabelPreview() {
  return (
    <LabelFrame>
      <LabelBody>
        <GenericBarcode />
        <MainId>{SAMPLE_PALLET_NUMBER}</MainId>
      </LabelBody>
    </LabelFrame>
  );
}

function DetailedLabelPreview() {
  return (
    <LabelFrame>
      <LabelHeader>{SAMPLE_HEADER}</LabelHeader>
      <LabelRule />
      <LabelBody>
        <GenericBarcode />
        <MainId>{SAMPLE_PALLET_NUMBER}</MainId>
      </LabelBody>
      <DetailFooter>
        <DetailCell>
          <DetailLabel>Rack</DetailLabel>
          <DetailValue>{SAMPLE_PARTS.rack}</DetailValue>
        </DetailCell>
        <DetailCell>
          <DetailLabel>Bay</DetailLabel>
          <DetailValue>{SAMPLE_PARTS.bay}</DetailValue>
        </DetailCell>
        <DetailCell>
          <DetailLabel>Level</DetailLabel>
          <DetailValue>{SAMPLE_PARTS.level}</DetailValue>
        </DetailCell>
      </DetailFooter>
    </LabelFrame>
  );
}

export type PalletLabelFormatValue = (typeof labelFormats)[keyof typeof labelFormats];

type PalletLabelFormatSelectorProps = {
  value?: PalletLabelFormatValue;
  onChange?: (value: PalletLabelFormatValue) => void;
};

export function PalletLabelFormatSelector({
  value,
  onChange,
}: PalletLabelFormatSelectorProps) {
  const isMinimal = value === labelFormats.Minimal;
  const isDetailed = value === labelFormats.Detailed;

  return (
    <PreviewWrap role="radiogroup" aria-label="Label format">
      <PreviewCard
        type="button"
        $active={isMinimal}
        aria-pressed={isMinimal}
        aria-label="Minimal label format"
        onClick={() => onChange?.(labelFormats.Minimal)}
      >
        <PreviewCaption type="secondary">Minimal</PreviewCaption>
        <MinimalLabelPreview />
      </PreviewCard>
      <PreviewCard
        type="button"
        $active={isDetailed}
        aria-pressed={isDetailed}
        aria-label="Detailed label format"
        onClick={() => onChange?.(labelFormats.Detailed)}
      >
        <PreviewCaption type="secondary">Detailed</PreviewCaption>
        <DetailedLabelPreview />
      </PreviewCard>
    </PreviewWrap>
  );
}
